import { SupplierRelationshipFulfillmentType } from "@sellar-io/api-v2/types";
import {
  calculateDeliveryOffers,
  findBiggestApplicableVolumeDiscounts,
  isCourierIntegrationFulfillmentType,
} from "@sellar-io/common";
import {
  ReactElement,
  ReactNode,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, Outlet, useMatch, useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { BasketDrawer } from "../../components";
import { ErrorMessage } from "../../components/errorMessage/ErrorMessage";
import {
  CATEGORY_BUYER,
  DISCOVER,
  ORDER_BUYER,
  SUPPLIER,
  SUPPLIERS,
} from "../../constants/routes";
import { StockListContext } from "../../contexts/StockListContext";
import {
  BasketContextActionEnum,
  ContextOrderBasketType,
  useBasketContext,
  useEvents,
  useSellarContext,
  useSellarUser,
} from "../../hooks";
import { useOpenOrderList } from "../../hooks/api/v2/order/useOpenOrderList";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { useOrderLinkFlowStorefront } from "../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowStorefront";
import { useOrderFlowTokenBasket } from "../../hooks/api/v2/orderLinkFlow/useOrderFlowTokenBasket";
import { useBuyerProduct } from "../../hooks/api/v2/product/useBuyerProduct";
import { useStockList } from "../../hooks/api/v2/stockList/useStockList";
import { useSupplier } from "../../hooks/api/v2/supplier/useSupplier";
import { useSupplierStorefrontV2 } from "../../hooks/api/v2/supplier/useSupplierStorefrontV2";
import { useLocationHistory } from "../../hooks/useLocationHistory.shared";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useMarketStorefrontSessionId } from "../../hooks/useMarketStorefrontSessionTracking";
import {
  useAncestorRouteParams,
  useRouteParams,
} from "../../hooks/useRouteParams";
import { SelectedFulfillmentType } from "./categoryPage/CategoryPageNavigationBar";
import { useStorefrontFilterStore } from "./StorefrontFilterStore";
import { useStockListContext } from "./useStockListContext";
import { StorefrontPromotionProvider } from "./categoryPage/Storefront/StorefrontPromotion/StorefrontPromotionProvider";
import { useFulfillmentTypeList } from "../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useOrderLinkFlowOrder } from "~/hooks/api/v2/orderLinkFlow/useOrderLinkFlowOrder";
import * as ROUTES from "../../constants/routes";

export type CheckoutSelectedFulfillmentType =
  | SupplierRelationshipFulfillmentType
  | undefined;

export const StockListProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const supplierRouteParams = useAncestorRouteParams(SUPPLIER);
  const supplierIdOrUrlId = supplierRouteParams?.supplierIdOrUrlId;
  const routeCategoryId = useRouteParams(CATEGORY_BUYER)?.categoryId;
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("categoryId") ?? routeCategoryId;
  const orderFlowToken = searchParams.get("orderFlowToken");
  const isSignedInUser = Boolean(user);

  const [, dispatch] = useSellarContext();
  const navigate = useNavigate();
  const { selectedPriceTier, selectedAvailabilityTier } =
    useStorefrontFilterStore(
      useShallow((state) => ({
        selectedPriceTier: state.selectedPriceTier,
        selectedAvailabilityTier: state.selectedAvailabilityTier,
      })),
    );

  const [basketState, dispatchBasket] = useBasketContext();
  const contextOrder = basketState.order;
  const prevOrderRef = useRef<ContextOrderBasketType | undefined>(undefined);

  const [selectedFulfillmentType, setSelectedFulfillmentType] =
    useState<SelectedFulfillmentType>();

  const [unAuthorisedUserPostcode, setUnAuthorisedUserPostcode] =
    useLocalStorage<string | undefined>("unauthorisedUserPostcode", undefined);

  const [checkoutSelectedFulfillmentType, setCheckoutSelectedFulfillmentType] =
    useState<CheckoutSelectedFulfillmentType>();

  const order = contextOrder;

  const orderLinkFlowStorefrontQuery = useOrderLinkFlowStorefront(
    isSignedInUser ? null : orderFlowToken,
    {
      enabled: !isSignedInUser && Boolean(orderFlowToken),
    },
  );

  const orderLinkFlowQuery = useOrderLinkFlowOrder(
    isSignedInUser ? null : orderFlowToken,
    {
      enabled: !isSignedInUser && Boolean(orderFlowToken),
    },
  );

  const supplierIdOrUrlIdForQuery =
    supplierIdOrUrlId ??
    orderLinkFlowStorefrontQuery.data?.storefront.supplierUrlId ??
    business?.Supplier?.urlId;

  const storefrontLinkFromUrl =
    searchParams.get("storefrontLink") ?? searchParams.get("priceTierLink");
  const storefrontLinkForPage = storefrontLinkFromUrl;

  const orderApi = useOrder(order?.id ?? 0, {
    enabled: !!order?.id && isSignedInUser,
  });

  const submittedOrder = orderApi.data;

  const productApi = useBuyerProduct(
    parseInt(categoryId?.toString() ?? "0", 10),
    {
      enabled: !!categoryId,
    },
  );

  const supplierQuery = useSupplier(supplierIdOrUrlIdForQuery, {
    enabled: !!supplierIdOrUrlIdForQuery,
    onError: (error) => {
      if (error.response?.status === 404) navigate(SUPPLIERS);
    },
  });

  useOrderFlowTokenBasket();

  useEffect(() => {
    if (isSignedInUser) {
      return;
    }

    if (
      !orderFlowToken ||
      !orderLinkFlowStorefrontQuery.data ||
      !orderLinkFlowQuery.data
    ) {
      return;
    }

    const redirectUrl = `${ROUTES.ORDER_SUMMARY}?token=${orderFlowToken}`;
    const retailerHasBusinessUser =
      orderLinkFlowQuery.data.retailerBusinessUsers.length > 0;

    if (retailerHasBusinessUser) {
      navigate(redirectUrl);
    }
  }, [
    isSignedInUser,
    navigate,
    orderFlowToken,
    orderLinkFlowQuery.data,
    orderLinkFlowStorefrontQuery.data,
  ]);

  if (supplierQuery.error) {
    const apiV2Error = apiV2AssertError(supplierQuery.error);
    if (
      apiV2Error?.response?.data.message === "Supplier not found" &&
      apiV2Error.response.status === 404
    ) {
      navigate(DISCOVER, { replace: true });
    }

    if (
      apiV2Error?.response?.data.message === "Supplier unavailable" &&
      apiV2Error.response.status === 404
    ) {
      navigate(DISCOVER, { replace: true });
    }
  }

  const isSupplierViewingOwnStorefront =
    business?.Supplier?.id === supplierQuery.data?.id;

  const getUnauthorisedUserPostcodeStorefrontParams = () => {
    if (isSupplierViewingOwnStorefront) {
      if (selectedFulfillmentType === "direct")
        return {
          UnAuthoriseUserPostcode: business?.DeliveryAddress.postalCode,
        };
      //unless the fulfillment type is direct, never set postcode
      return;
    }
    if (unAuthorisedUserPostcode)
      return {
        UnAuthoriseUserPostcode: unAuthorisedUserPostcode,
      };
    return;
  };

  const [editing, setEditing] = useState(false);

  const orderFulfillmentType = () => {
    if (isCourierIntegrationFulfillmentType(orderApi.data?.fulfillmentType))
      return "courier";
    return orderApi.data?.fulfillmentType;
  };

  const supplierStorefront = useSupplierStorefrontV2(
    supplierQuery.data?.id ?? 0,
    {
      ...((orderApi.data?.fulfillmentType ||
        checkoutSelectedFulfillmentType) && {
        fulfillmentType:
          checkoutSelectedFulfillmentType ?? orderFulfillmentType(),
      }),
      PriceTierLink: storefrontLinkForPage ?? undefined,
      ...(isSupplierViewingOwnStorefront &&
        selectedPriceTier && {
          selectedPriceTierId: selectedPriceTier,
        }),
      ...(isSupplierViewingOwnStorefront &&
        selectedAvailabilityTier && {
          selectedAvailabilityTierId: selectedAvailabilityTier,
        }),
      ...(isSupplierViewingOwnStorefront &&
        selectedFulfillmentType && {
          fulfillmentType:
            selectedFulfillmentType === "direct" ? "direct" : "courier",
        }),
      ...(orderLinkFlowStorefrontQuery.data?.storefront
        .supplierRetailerConnectionId && {
        OrderFlowToken: {
          SupplierRetailerConnectionId:
            orderLinkFlowStorefrontQuery.data.storefront
              .supplierRetailerConnectionId,
        },
      }),
      ...getUnauthorisedUserPostcodeStorefrontParams(),
    },
    { enabled: !!supplierQuery.data },
  );

  const priceTierDiscounts = useMemo(
    () => supplierStorefront.data?.VolumeDiscounts?.PriceTierDiscounts ?? [],
    [supplierStorefront.data?.VolumeDiscounts?.PriceTierDiscounts],
  );

  const basketedProductsQuery = useStockList(
    {
      active: true,
      SupplierId: supplierQuery.data?.id ?? 0,
      id: order
        ? order.OrderItems?.map((i) => i.ProductId || i.Product.id)
        : [],
      storefrontLinkPassword: storefrontLinkForPage ?? undefined,
      postcode: unAuthorisedUserPostcode,
    },
    {
      //this needs to be enabled for basketed items to check their current price (don't disable if user exists)
      enabled: !!(supplierQuery.data?.id && order?.OrderItems),
    },
  );

  // Initialize, set title of page etc.
  useEffect(() => {
    return () => {
      dispatchBasket({
        type: BasketContextActionEnum.RESET,
      });
    };
  }, [dispatch, dispatchBasket]);

  useEffect(() => {
    if (order && order.OrderItems && order.OrderItems.length > 0) {
      if (priceTierDiscounts.length > 0) {
        const biggestApplicableVolumeDiscounts =
          findBiggestApplicableVolumeDiscounts({
            volumeDiscountList: priceTierDiscounts,
            caseSizeRuleList: supplierStorefront.data?.CaseSizeRules.list ?? [],
            orderItemList: order.OrderItems.map((orderItem) => ({
              ...orderItem,
              packQuantity: orderItem.Product.packQuantity ?? null,
              volume: orderItem.Product.volume ?? null,
              unit: orderItem.Product.unit ?? null,
              containerType: orderItem.Product.containerType ?? null,
            })),
          });

        const totalVolumeDiscountAmount =
          biggestApplicableVolumeDiscounts.reduce(
            (total, discount) => total + discount.discountAmount,
            0,
          );

        if (
          biggestApplicableVolumeDiscounts.length &&
          totalVolumeDiscountAmount !== order.discountAmount
        ) {
          dispatchBasket({
            type: BasketContextActionEnum.CHANGE_DISCOUNT,
            discount: {
              totalVolumeDiscountAmount,
              list: biggestApplicableVolumeDiscounts.map((discount) => ({
                PriceTierDiscountId: discount.volumeDiscount.id,
              })),
            },
          });
        }
      }
    }
  }, [
    order,
    dispatchBasket,
    priceTierDiscounts,
    supplierStorefront.data?.CaseSizeRules.list,
  ]);

  // Reload product list when basket has changed, here can get most recent PriceTier information
  const updateDraftWithServerProducts = useEffectEvent(async () => {
    if (order?.OrderItems?.length) {
      // refetch the query so if price tier is updated and buyer is editing, the correct messages appear
      const query = await basketedProductsQuery.refetch();
      const basketedProducts = query.data?.pages[0];
      let updated = 0;

      const updatedOrderItems = order.OrderItems.map((orderItem) => {
        const newProd = basketedProducts?.find(
          (basketedProduct) =>
            basketedProduct.id ===
            (orderItem.ProductId || orderItem.Product?.id),
        );
        const isPromotionalRewardItem =
          orderItem.PromotionRewardItemId && orderItem.promotionAppliedAt;

        if (newProd) {
          if (
            newProd.ProductPriceTier?.price !== orderItem.price &&
            !isPromotionalRewardItem
          ) {
            updated++;
          }
          if (
            newProd.ProductPriceTier &&
            newProd.ProductPriceTier.price !== orderItem.price &&
            !isPromotionalRewardItem
          ) {
            updated++;
          }
          return {
            ...orderItem,
            price:
              newProd.ProductPriceTier && !isPromotionalRewardItem
                ? newProd.ProductPriceTier.price
                : orderItem.price,
            Product: newProd,
          };
        }

        return orderItem;
      });

      if (updated) {
        dispatchBasket({
          type: BasketContextActionEnum.CHANGE_ORDER,
          order: { ...order, OrderItems: updatedOrderItems },
        });
      }
    }

    if (order) {
      prevOrderRef.current = order;
    }
  });

  useEffect(() => {
    if (supplierQuery.data && order && basketedProductsQuery.data) {
      if (
        !prevOrderRef.current ||
        JSON.stringify(order.OrderItems?.map((i) => i.Product.id)) !=
          JSON.stringify(
            prevOrderRef.current.OrderItems?.map((i) => i.Product.id),
          )
      ) {
        updateDraftWithServerProducts();
      }
    }
  }, [basketedProductsQuery.data, order, supplierQuery.data]);

  useEffect(() => {
    if (supplierQuery.data?.id)
      localStorage.setItem(
        "lastViewedSupplierId",
        supplierQuery.data.id.toString(),
      );
    else localStorage.removeItem("lastViewedSupplierId");
  }, [supplierQuery.data?.id]);

  const initialiseStorefrontOrder = useEffectEvent(() => {
    const storefrontSupplierId = supplierQuery.data?.id;
    const isUnifiedCheckoutOrder = Boolean(
      order?.sdBasketUuid || order?.sdBasketId,
    );
    const isOrderForStorefront =
      !isUnifiedCheckoutOrder &&
      (!storefrontSupplierId ||
        !order?.SupplierId ||
        order.SupplierId === storefrontSupplierId);
    const supplierOrder = isOrderForStorefront ? order : null;
    const supplierId = supplierOrder?.SupplierId ?? storefrontSupplierId ?? 0;
    const connectionVatPercentage =
      supplierStorefront.data?.SupplierRetailerConnection?.vatPercentage;
    const hasConnectionVatPercentage =
      !!connectionVatPercentage || connectionVatPercentage === 0;
    const vatPercentage = hasConnectionVatPercentage
      ? connectionVatPercentage
      : supplierQuery.data?.vatPercentage;
    const retailerId = supplierOrder?.RetailerId || business?.Retailer?.id || 0;

    dispatchBasket({
      type: BasketContextActionEnum.CHANGE_ORDER,
      order: {
        ...supplierOrder,
        RetailerId: retailerId,
        SupplierId: supplierId,
        initiator: supplierOrder?.initiator ?? "retailer",
        fulfillmentType:
          supplierOrder?.fulfillmentType ??
          (isOrderForStorefront ? contextOrder?.fulfillmentType : undefined),
        vatPercentage: vatPercentage,
        currency: supplierQuery.data?.currency,
      },
    });
  });

  useEffect(() => {
    initialiseStorefrontOrder();
  }, [
    business,
    dispatchBasket,
    supplierQuery.data,
    supplierStorefront.data?.SupplierRetailerConnection?.vatPercentage,
  ]);

  useEffect(() => {
    if (!supplierQuery.data?.id) return;

    dispatchBasket({
      type: BasketContextActionEnum.CHANGE_SUPPLIER_RETAILER_IDS,
      // Sets to zero if the user is adding to their basket when unauthenticated
      RetailerId: business?.Retailer?.id ?? 0,
      SupplierId: supplierQuery.data.id,
    });
  }, [business?.Retailer?.id, dispatchBasket, supplierQuery.data?.id]);

  const openOrderList = useOpenOrderList(
    {
      limit: 20,
      query: [
        {
          type: "SupplierId",
          modifier: "EQUALS",
          value: supplierQuery.data?.id ?? 0,
        },
      ],
    },
    {
      enabled: !!business?.Retailer && !!supplierQuery.data?.id,
    },
  );

  const containerTypeDeliveryChargeRules =
    supplierStorefront.data?.FulfillmentType.ContainerTypeDeliveryChargeRules;

  const orderItems =
    order?.OrderItems?.map((oi) => {
      return {
        containerType: oi.Product.containerType ?? "",
        quantity: oi.quantity,
        price: oi.price,
      };
    }) ?? [];
  const calculatedDeliveryOffers = containerTypeDeliveryChargeRules
    ? calculateDeliveryOffers(containerTypeDeliveryChargeRules, orderItems)
    : [];

  const fulfillmentTypeCourierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplierQuery.data?.id ?? 0,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "courier",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: !!supplierQuery.data?.id,
    },
  );

  const courierFulfillmentType =
    fulfillmentTypeCourierQuery.data?.pages[0]?.[0];

  const fulfillmentTypeHaulierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplierQuery.data?.id ?? 0,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "haulier",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: !!supplierQuery.data?.id,
    },
  );

  const haulierFulfillmentType =
    fulfillmentTypeHaulierQuery.data?.pages[0]?.[0];

  const connection = supplierStorefront.data?.SupplierRetailerConnection
    ? {
        approved: supplierStorefront.data?.SupplierRetailerConnection.approved,
        fulfillmentType:
          supplierStorefront.data?.SupplierRetailerConnection.fulfillmentType,
      }
    : undefined;

  // Quantity in stock, regardless of basket

  return (
    <StockListContext.Provider
      value={{
        openOrderList: openOrderList.data?.pages[0] ?? [],
        supplierQuery,
        productApi,
        categoryId,
        supplierIdOrUrlId,
        connection,
        setEditing,
        editing,
        submittedOrder,
        supplierStorefront: supplierStorefront.data,
        isStorefrontLoading:
          supplierStorefront?.isPending ||
          (!isSignedInUser &&
            !!orderFlowToken &&
            orderLinkFlowStorefrontQuery.isPending),
        basketedProductsQuery,
        selectedFulfillmentType,
        setSelectedFulfillmentType,
        setUnAuthorisedUserPostcode,
        unAuthorisedUserPostcode,
        calculatedDeliveryOffers,
        courierFulfillmentType,
        haulierFulfillmentType,
        setCheckoutSelectedFulfillmentType,
        checkoutSelectedFulfillmentType,
      }}
    >
      {children}
    </StockListContext.Provider>
  );
};

export const OrderRedirect = (): ReactElement => {
  const supplierIdOrUrlId = useMatch(SUPPLIER.pattern)?.params
    .supplierIdOrUrlId;
  if (!supplierIdOrUrlId) return <Navigate to={SUPPLIERS} replace />;

  return <Navigate to={ORDER_BUYER.href({ supplierIdOrUrlId })} replace />;
};

const StockListLayout = () => {
  const { supplierQuery } = useStockListContext();

  const { track } = useEvents();
  const supplierIdOrUrlId = useMatch({
    path: SUPPLIER.pattern,
    end: false,
  })?.params.supplierIdOrUrlId;
  const { business } = useSellarCurrentBusiness();
  const [searchParams] = useSearchParams();
  const { sessionId, getSessionHistory } = useMarketStorefrontSessionId();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isLargeScreen = useMediaQuery("(min-width: 1900px)");
  const locationHistory = useLocationHistory();

  // Helper function to check if a route is a storefront route
  const isStorefrontRoute = (pathname: string): boolean => {
    const storefrontPattern = /^\/suppliers\/[^/]+\/order/;
    return storefrontPattern.test(pathname);
  };

  const trackStorefrontPageView = useEffectEvent(() => {
    // Track storefront page view only on the first storefront visit in this session
    if (sessionId && business?.type !== "supplier") {
      const sessionHistory = getSessionHistory();
      const storefrontVisitsInSession = sessionHistory.filter((visit) =>
        isStorefrontRoute(visit.route),
      ).length;

      // Only track if this is the first storefront visit in the session
      if (storefrontVisitsInSession === 1) {
        track("Storefront Page Viewed", {
          supplier: supplierIdOrUrlId,
          layout: isMobile
            ? "cards-compact"
            : isLargeScreen
              ? "table"
              : "cards",
          isStorefrontLink:
            !!searchParams.get("storefrontLink") ||
            !!searchParams.get("priceTierLink"),
          previousUrl: locationHistory[0],
          sessionId,
        });
      }
    }
  });

  useEffect(() => {
    trackStorefrontPageView();
  }, [sessionId]);

  const supplierName = supplierQuery.data?.Business.name;
  const supplierDescription = supplierQuery.data?.description ?? "";

  useEffect(() => {
    if (!supplierName) return;

    const previousTitle = document.title;
    document.title = `${supplierName} on Sellar - The easiest way to buy direct`;

    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription =
      descriptionMeta?.getAttribute("content") ?? null;
    descriptionMeta?.setAttribute("content", supplierDescription);

    return () => {
      document.title = previousTitle;
      if (descriptionMeta && previousDescription !== null) {
        descriptionMeta.setAttribute("content", previousDescription);
      }
    };
  }, [supplierName, supplierDescription]);

  if (supplierQuery.error) return <ErrorMessage />;

  return (
    <>
      <Outlet />
      <BasketDrawer />
    </>
  );
};

export const StockListPage = () => {
  return (
    <StockListProvider>
      <StorefrontPromotionProvider>
        <StockListLayout />
      </StorefrontPromotionProvider>
    </StockListProvider>
  );
};
